/**
 * Merges a saved column order with the current natural column order.
 * Columns present in the saved state keep their position (preserving user customisation).
 * New columns (absent from saved state) are inserted at their natural position.
 * Removed columns (present in saved state but absent from natural order) are dropped.
 */
export const mergeColumnOrder = <T extends { field: string }>(defaultOrderedColumns: T[], savedOrderedFields: string[]): T[] => {
  // Deduplicate first to guard against corrupt/older localStorage state.
  const uniqueSavedFields = [...new Set(savedOrderedFields)]

  // Start with saved order, dropping columns that no longer exist.
  const orderedColumns: T[] = uniqueSavedFields
    .map((field) => defaultOrderedColumns.find((column) => column.field === field))
    .filter((column): column is T => column !== undefined)

  // Insert new columns at their natural position
  for (const [defaultIndex, column] of defaultOrderedColumns.entries()) {
    const { field } = column
    if (savedOrderedFields.includes(field)) continue
    const insertAt =
      orderedColumns.findLastIndex(({ field: orderedField }) => {
        const orderedDefaultIndex = defaultOrderedColumns.findIndex((column) => column.field === orderedField)
        return orderedDefaultIndex < defaultIndex
      }) + 1
    orderedColumns.splice(insertAt, 0, column)
  }

  return orderedColumns
}
